import React from 'react';
import ReactDOM from 'react-dom/client';
import CustomModal from '../components/CustomModal';

// Global modal state
let modalRoot = null;
let currentModal = null;

const createModalRoot = () => {
    if (!modalRoot) {
        const div = document.createElement('div');
        div.id = 'custom-modal-root';
        document.body.appendChild(div);
        modalRoot = ReactDOM.createRoot(div);
    }
    return modalRoot;
};

const showModal = (config) => {
    return new Promise((resolve) => {
        const modalRoot = createModalRoot();

        const handleConfirm = () => {
            if (config.onConfirm) {
                const result = config.onConfirm();
                resolve({ isConfirmed: true, isDenied: false, isDismissed: false, value: result });
            } else {
                resolve({ isConfirmed: true, isDenied: false, isDismissed: false });
            }
            closeModal();
        };

        const handleCancel = () => {
            if (config.onCancel) {
                config.onCancel();
            }
            resolve({ isConfirmed: false, isDenied: false, isDismissed: true });
            closeModal();
        };

        const handleClose = () => {
            resolve({ isConfirmed: false, isDenied: false, isDismissed: true });
            closeModal();
        };

        // Determine default button color: purple for validation errors (warning), green for others
        const defaultConfirmButtonColor = config.icon === 'warning' ? '#b38ec1' : '#addd1e';
        
        const modalConfig = {
            show: true,
            title: config.title || '',
            text: config.text || '',
            html: config.html || null,
            icon: config.icon || null,
            imageUrl: config.imageUrl || null,
            errorText: config.errorText || null,
            headerColor: config.headerColor || null,
            showConfirmButton: config.showConfirmButton !== false,
            showCancelButton: config.showCancelButton || false,
            confirmButtonText: config.confirmButtonText || 'OK',
            cancelButtonText: config.cancelButtonText || 'Cancel',
            confirmButtonColor: config.confirmButtonColor || defaultConfirmButtonColor,
            cancelButtonColor: config.cancelButtonColor || '#d33',
            onConfirm: handleConfirm,
            onCancel: handleCancel,
            onClose: handleClose,
            timer: config.timer || null,
            timerProgressBar: config.timerProgressBar || false,
            allowOutsideClick: config.allowOutsideClick !== false,
            allowEscapeKey: config.allowEscapeKey !== false,
            ...config
        };

        const ModalComponent = () => <CustomModal {...modalConfig} />;
        modalRoot.render(<ModalComponent />);
    });
};

const closeModal = () => {
    if (modalRoot) {
        modalRoot.render(null);
    }
};

// SweetAlert-like API
export const Modal = {
    fire: (titleOrConfig, text, icon) => {
        // Handle simple string format: Modal.fire("Error", "message", "error")
        if (typeof titleOrConfig === 'string') {
            return showModal({
                title: titleOrConfig || '',
                text: text || '',
                icon: icon || 'info',
                showCancelButton: false
            });
        }

        // Handle object format
        return showModal(titleOrConfig);
    },

    // Convenience methods
    success: (title, text) => {
        return showModal({
            title,
            text,
            icon: 'success',
            showCancelButton: false
        });
    },

    error: (title, text) => {
        return showModal({
            title,
            text,
            icon: 'error',
            showCancelButton: false
        });
    },

    warning: (title, text) => {
        return showModal({
            title,
            text,
            icon: 'warning',
            showCancelButton: false
        });
    },

    info: (title, text) => {
        return showModal({
            title,
            text,
            icon: 'info',
            showCancelButton: false
        });
    },

    confirm: (config) => {
        return showModal({
            ...config,
            showCancelButton: true
        });
    }
};

export default Modal;

